import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Package,
  Library,
  FolderOpen,
  Palette,
  HelpCircle,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Plus,
  Shield,
  Users,
  Lock,
  Sparkles,
  Wand2,
  Clapperboard,
  Printer,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionMonitor } from '../../hooks/useSessionMonitor';
import { getLicenseSummary, hasAgencyAccess, hasProfessionalAccess } from '../../utils/licensing';
import Button from '../Button';

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
  badgeVariant?: 'primary' | 'upgrade';
}

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, impersonation, exitImpersonation } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isRestoringAdmin, setIsRestoringAdmin] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useSessionMonitor();

  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const hasAgencyLicense = hasAgencyAccess(profile);
  const hasProfessionalLicense = hasProfessionalAccess(profile);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const navSections = useMemo(() => {
    const sections: Array<{ title: string; items: NavItem[] }> = [
      {
        title: 'Priority',
        items: [
          {
            name: 'Brand Team',
            icon: <Sparkles className="w-5 h-5" />,
            path: '/dashboard/brand-team',
            badge: 'New',
            badgeVariant: 'primary',
          },
          {
            name: 'Brand Craft',
            icon: <Wand2 className="w-5 h-5" />,
            path: '/dashboard/brand-craft',
            badge: 'New',
            badgeVariant: 'primary',
          },
          {
            name: 'Your Brand',
            icon: <Palette className="w-5 h-5" />,
            path: '/dashboard/brand',
          },
        ],
      },
      {
        title: 'Workspace',
        items: [
          {
            name: 'Templates',
            icon: <Package className="w-5 h-5" />,
            path: '/dashboard/mockups',
          },
          {
            name: 'Library',
            icon: <Library className="w-5 h-5" />,
            path: '/dashboard/library',
          },
          {
            name: 'Mockup to Print',
            icon: <Printer className="w-5 h-5" />,
            path: '/dashboard/mockup-to-print',
            badge: 'New',
            badgeVariant: 'primary',
          },
          {
            name: 'Assets',
            icon: <FolderOpen className="w-5 h-5" />,
            path: '/dashboard/assets',
          },
          {
            name: 'SceneScaper',
            icon: hasProfessionalLicense ? <Sparkles className="w-5 h-5" /> : <Lock className="w-5 h-5" />,
            path: '/dashboard/scenescaper',
            badge: hasProfessionalLicense ? undefined : 'Upgrade to Pro',
            badgeVariant: hasProfessionalLicense ? undefined : 'upgrade',
          },
          {
            name: 'Video Mockups',
            icon: hasProfessionalLicense ? <Clapperboard className="w-5 h-5" /> : <Lock className="w-5 h-5" />,
            path: '/dashboard/video-mockups',
            badge: hasProfessionalLicense ? 'New' : 'Upgrade to Pro',
            badgeVariant: hasProfessionalLicense ? 'primary' : 'upgrade',
          },
        ],
      },
      {
        title: 'Business',
        items: [
          {
            name: 'Agency',
            icon: hasAgencyLicense ? <Users className="w-5 h-5" /> : <Lock className="w-5 h-5" />,
            path: '/dashboard/agency',
            badge: hasAgencyLicense ? undefined : 'Upgrade',
            badgeVariant: hasAgencyLicense ? undefined : 'upgrade',
          },
          {
            name: 'Help & Support',
            icon: <HelpCircle className="w-5 h-5" />,
            path: '/dashboard/support',
          },
        ],
      },
    ];

    if (isAdmin) {
      sections.push({
        title: 'Admin',
        items: [
          {
            name: 'Admin Panel',
            icon: <Shield className="w-5 h-5" />,
            path: '/admin',
            badge: 'Admin',
            badgeVariant: 'primary',
          },
        ],
      });
    }

    return sections;
  }, [hasAgencyLicense, hasProfessionalLicense, isAdmin]);

  const displayName =
    profile?.full_name ||
    (user as any)?.user_metadata?.full_name ||
    (user as any)?.user_metadata?.name ||
    'User';
  const displayEmail = user?.email || '';
  const userInitial = (displayName || displayEmail || 'U').toString().charAt(0).toUpperCase();
  const licenseLabel = getLicenseSummary(profile);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleExitImpersonation = async () => {
    try {
      setIsRestoringAdmin(true);
      setRestoreError(null);
      const returnPath = impersonation?.adminReturnPath || '/admin/users';
      await exitImpersonation();
      window.location.assign(returnPath);
    } catch (error: any) {
      console.error('Exit impersonation error:', error);
      setRestoreError(error.message || 'Failed to restore the admin session');
    } finally {
      setIsRestoringAdmin(false);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const renderNavSection = (title: string, items: NavItem[]) => (
    <div key={title} className="space-y-2">
      <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
        {title}
      </p>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.path}>
            <Link
              to={item.path}
              className={`
                group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all
                ${isActivePath(item.path)
                  ? 'bg-primary-50 text-brand-purple shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }
              `}
            >
              <span className={isActivePath(item.path) ? 'text-brand-purple' : 'text-gray-400 group-hover:text-gray-500'}>
                {item.icon}
              </span>
              <span className="ml-3 flex-1">{item.name}</span>
              {item.badge && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    item.badgeVariant === 'upgrade'
                      ? 'border border-orange-200 bg-orange-100 text-orange-700'
                      : 'bg-brand-purple text-white'
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {isActivePath(item.path) && <ChevronRight className="ml-2 h-4 w-4 text-brand-purple" />}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  const userMenu = (
    <div className="absolute bottom-full left-4 right-4 mb-3 rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
      <button
        onClick={() => {
          navigate('/dashboard/settings');
          setIsUserMenuOpen(false);
        }}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
      >
        <Settings className="h-4 w-4 text-gray-400" />
        <span className="flex-1">User Settings</span>
      </button>
      <button
        onClick={handleLogout}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
      >
        <LogOut className="h-4 w-4" />
        <span className="flex-1">Sign Out</span>
      </button>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5 pb-4">
          <Link to="/dashboard" className="flex flex-shrink-0 items-center px-4">
            <img src="/clawbrand_logo_color.png" alt="ClawBrand AI" className="h-10 w-auto" />
          </Link>

          <div className="mt-6 px-4">
            <Button to="/dashboard/create" variant="primary" size="medium" className="w-full">
              <Plus className="mr-2 h-5 w-5" />
              Create New Mockup
            </Button>
          </div>

          <nav className="mt-6 flex-1 space-y-6 px-3">
            {navSections.map((section) => renderNavSection(section.title, section.items))}
          </nav>

          <div className="relative flex-shrink-0 border-t border-gray-200 px-4 py-4">
            {isUserMenuOpen && userMenu}
            <button
              onClick={() => setIsUserMenuOpen((current) => !current)}
              className="flex w-full items-center rounded-2xl border border-transparent px-2 py-2 text-left transition-all hover:border-gray-200 hover:bg-gray-50"
            >
              <div className="flex-shrink-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple text-sm font-medium text-white">
                  {userInitial}
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
                <p className="truncate text-xs text-gray-500">{displayEmail}</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">{licenseLabel}</p>
              </div>
              <ChevronDown className={`ml-3 h-4 w-4 text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="fixed top-0 left-0 right-0 z-40 border-b border-gray-200 bg-white md:hidden">
        <div className="flex h-16 items-center justify-between px-4">
          <Link to="/dashboard">
            <img src="/clawbrand_logo_color.png" alt="ClawBrand AI" className="h-8 w-auto" />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-gray-600 bg-opacity-75 md:hidden" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="overflow-y-auto pt-20 pb-4">
              <nav className="space-y-6 px-3">
                {navSections.map((section) => renderNavSection(section.title, section.items))}
              </nav>

              <div className="relative mt-8 border-t border-gray-200 px-4 py-4">
                {isUserMenuOpen && userMenu}
                <button
                  onClick={() => setIsUserMenuOpen((current) => !current)}
                  className="flex w-full items-center rounded-2xl border border-transparent px-2 py-2 text-left transition-all hover:border-gray-200 hover:bg-gray-50"
                >
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple text-sm font-medium text-white">
                      {userInitial}
                    </div>
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
                    <p className="truncate text-xs text-gray-500">{displayEmail}</p>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">{licenseLabel}</p>
                  </div>
                  <ChevronDown className={`ml-3 h-4 w-4 text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {impersonation && (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
            <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Admin impersonation active
                </p>
                <p className="text-sm text-amber-800">
                  You are viewing {impersonation.targetName || impersonation.targetEmail} as an end user.
                </p>
                {restoreError && (
                  <p className="mt-1 text-xs font-medium text-red-700">{restoreError}</p>
                )}
              </div>
              <button
                onClick={handleExitImpersonation}
                disabled={isRestoringAdmin}
                className="inline-flex items-center justify-center rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isRestoringAdmin ? 'Restoring Admin Session...' : 'Return to Admin'}
              </button>
            </div>
          </div>
        )}
        <main className="flex-1 overflow-y-auto bg-gray-50 pt-16 md:pt-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
